/**
 * <IconButton> — a pressable icon with no text label (Buttons family, L0.1).
 *
 * The maison replacement for the legacy `IconButtonM` (react-native-paper). Built on
 * `Pressable`, it composes the `<Icon>` atom for the glyph and reads every color from
 * the runtime theme — zero hex literals.
 *
 * Defaults mirror the circular action buttons in the app today (see the
 * `renderListFooterComponent` "+" / archive buttons): a 50×50 circle with a ~20px icon.
 *
 * Shape & background:
 *  - `shape` → `circle` (default, `borderRadius = size / 2`) or `square` (`theme.radius.md`).
 *  - `appearance="transparent"` → no background, just the icon (tab buttons / header actions).
 *  - `appearance="filled"` (default) → solid fill from the `color` token.
 *
 * Pas de dégradé sur un bouton : la V13 réserve le dégradé aux temps forts (bordure de la
 * prochaine action, carte d'événement) et aux trois fonds d'écran. La variante `color` en
 * tableau + `<LinearGradient>` a donc été retirée — elle n'avait aucun appelant.
 */
import React from "react"
import { ActivityIndicator, Pressable, type GestureResponderEvent } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { inkOnFill, solidFill, type SemanticColorKey } from "../../../theme/tokens/colors"
import { FocusRing, focusRingHost } from "../../FocusRing"
import { Icon, type IconName } from "../../icons/Icon"

/**
 * `<Pressable>` ne type que `pressed` ; react-native-web ajoute `focused` à l'exécution.
 * Le déclarer optionnel laisse le callback accepter l'état natif tel quel.
 */
type PressableVisualState = { pressed: boolean; focused?: boolean }

/** Outer footprint — a circle (default) or a rounded square (header/tab actions). */
type IconButtonShape = "circle" | "square"
/** Whether the button paints a background (`filled`, driven by `color`) or none (`transparent`). */
type IconButtonAppearance = "filled" | "transparent"

export type IconButtonProps = {
    /** Nom de glyphe (union typée de l'atome `<Icon>`). */
    iconName: IconName
    /** Footprint in px — defaults to 50. */
    size?: number
    /** Fill when `appearance="filled"` — defaults to `primary`. Ignored when transparent. */
    color?: SemanticColorKey
    /** Glyph color token — défaut : l'encre de l'aplat (`inkOnFill`), donc blanc sur `primary` en clair. */
    iconColor?: SemanticColorKey
    /** Glyph size in px — defaults to `round(size * 0.4)` (=20 at size 50). */
    iconSize?: number
    /** `circle` (default, `borderRadius = size / 2`) or `square` (`theme.radius.md`). */
    shape?: IconButtonShape
    /** `filled` (default) → background from `color`; `transparent` → no background, icon only. */
    appearance?: IconButtonAppearance
    onPress?: (event: GestureResponderEvent) => void
    /** Dims the button and blocks presses. */
    disabled?: boolean
    /**
     * Son action est en cours : la roue prend la place du glyphe, à la même place, et le bouton
     * ne répond plus — la flèche « Rafraîchir » d'un en-tête pendant sa relecture. C'est un
     * indicateur d'ACTION : un contenu qui charge a son squelette, jamais une roue.
     */
    loading?: boolean
    /**
     * Un bouton à deux états (la main levée) : l'état se dit au lecteur d'écran (`selected`), pas
     * seulement par la couleur. Absent : un bouton d'action simple.
     */
    selected?: boolean
    /**
     * Agrandit la zone de toucher au-delà du bouton, sans changer sa forme : un bouton plus petit que
     * la cible du livre (44 px), posé seul (les boutons de la scène, 36 px). Défaut : aucune.
     */
    hitSlop?: number
    testID?: string
    accessibilityLabel?: string
}

export function IconButton(props: IconButtonProps) {
    const {
        iconName,
        size = 50,
        color = "primary",
        iconColor = inkOnFill(color),
        iconSize,
        shape = "circle",
        appearance = "filled",
        onPress,
        disabled = false,
        loading = false,
        selected,
        hitSlop,
        testID,
        accessibilityLabel,
    } = props

    const { theme } = useUnistyles()

    const radius = shape === "circle" ? size / 2 : theme.radius.md
    // L'aplat de bouton de la couleur (`danger` → `danger-fill`), ou rien quand le bouton n'en peint pas.
    const solidBg = appearance === "transparent" ? null : solidFill(color)
    const resolvedIconSize = iconSize ?? Math.round(size * 0.4)
    const isBlocked = disabled || loading

    return (
        <Pressable
            testID={testID}
            onPress={onPress}
            disabled={isBlocked}
            hitSlop={hitSlop}
            accessibilityRole="button"
            accessibilityLabel={accessibilityLabel}
            accessibilityState={{ disabled: isBlocked, busy: loading, ...(selected != null ? { selected } : {}) }}
            style={({ pressed }: PressableVisualState) => [
                styles.button(size, radius, solidBg, disabled),
                focusRingHost,
                pressed && !isBlocked ? styles.pressed : undefined,
            ]}
        >
            {({ focused }: PressableVisualState) => (
                <>
                    <FocusRing visible={focused === true && !isBlocked} radius={radius} />
                    {loading ? (
                        <ActivityIndicator size="small" color={theme.colors[iconColor]} />
                    ) : (
                        <Icon name={iconName} size={resolvedIconSize} color={iconColor} />
                    )}
                </>
            )}
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    button: (size: number, radius: number, bg: SemanticColorKey | null, disabled: boolean) => ({
        width: size,
        height: size,
        borderRadius: radius,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        backgroundColor: bg ? theme.colors[bg] : "transparent",
        opacity: disabled ? 0.5 : 1,
    }),
    pressed: {
        transform: [{ scale: 0.98 }],
    },
}))
